/** LOCKED TABLES — the cards this venue has fixed to one table.
 *
 *  Sits beside the table audit lines, which is where owners and super admins
 *  already look. Each row says which card, which table, who locked it and
 *  when, with one Unlock button. Unlocking is logged exactly like the staff
 *  screen's own switch.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import api from '../../lib/api';

export const LockedTables = ({ organizationId, neo }) => {
    const { t, i18n } = useTranslation();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState('');

    const load = useCallback(async () => {
        if (!organizationId) return;
        try {
            const res = await api.get('/game-results/locked-tables', {
                ...(neo || {}), params: { organization_id: organizationId },
            });
            setData(res.data);
        } catch (_e) { setData(null); }
    }, [organizationId, neo]);

    useEffect(() => { load(); }, [load]);

    const unlock = async (cardId) => {
        setBusy(cardId);
        try {
            await api.post('/game-results/locked-tables/unlock',
                { organization_id: organizationId, card_id: cardId }, neo || {});
            await load();
            toast.success(t('gameResults.unlocked'));
        } catch (_e) {
            toast.error(t('gameResults.unlockFailed'));
        } finally { setBusy(''); }
    };

    if (!data) return null;

    const when = (iso) => {
        if (!iso) return '';
        try {
            return new Date(iso).toLocaleString(i18n.language, {
                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
            });
        } catch (_e) { return iso; }
    };

    return (
        <Card data-testid="game-results-locked-tables">
            <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                    <Lock size={15} /> {t('gameResults.lockedTables')}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
                {(data.rows || []).length === 0 && (
                    <p className="text-sm text-muted-foreground" data-testid="locked-tables-empty">
                        {t('gameResults.lockedTablesEmpty')}
                    </p>
                )}
                {(data.rows || []).map((row) => (
                    <div key={row.card_id}
                         className="flex flex-wrap items-center justify-between gap-2 border-b last:border-0 py-2"
                         data-testid={`locked-table-${row.card_id}`}>
                        <div>
                            <p className="text-sm font-semibold">{row.label}</p>
                            <p className="text-[11px] text-muted-foreground">
                                {row.card_id} · {t('gameResults.lockedBy', { who: row.locked_by || '—' })}
                                {row.locked_at ? ` · ${when(row.locked_at)}` : ''}
                            </p>
                        </div>
                        {data.can_unlock && (
                            <Button type="button" variant="outline" size="sm"
                                    disabled={busy === row.card_id}
                                    onClick={() => unlock(row.card_id)}
                                    data-testid={`locked-table-unlock-${row.card_id}`}>
                                <Unlock size={13} className="mr-1" /> {t('gameResults.unlock')}
                            </Button>
                        )}
                    </div>
                ))}
            </CardContent>
        </Card>
    );
};

export default LockedTables;
